import { Button, DatePicker, Input, Space, TableProps } from "antd";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import ActionWatchIcon from "../../../../assets/icons/ActionWatchIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import OrangePlusIcon from "../../../../assets/icons/OrangePlusIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import LogViewModalKPI from "../../../../components/OwnKPI/LogViewModalKPI";
import CustomTable from "../../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../../constants/constants";
import { useGetOwnKpiByDepartmentQuery } from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import {
  GetOwnKpiDepartmentParams,
  GetOwnKpiDepartmentResponseType,
} from "../../../../types/performance";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray } from "../../../../utils/utils";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";

const { RangePicker } = DatePicker;

type OwnKpiListType = GetOwnKpiDepartmentResponseType["data"]["kpis"][0];

export const planTypeColor = {
  performance: "#D8A800",
  development: "#4A72FF",
};

export const statusList = {
  pending: {
    text: "Pending for Approval",
    style: "status-warning",
  },
  complete: {
    text: "Approved by Line Manager",
    style: "status-success",
  },
  reject: {
    text: "Reject by Line Manager",
    style: "status-danger",
  },
  default: {
    text: "KPI Setup Start",
    style: "status text-[#fe832b] bg-[#FFF5F4]",
  },
};

export default function OwnKPI() {
  const { open, handleClose, handleOpen } = useModal<
    { log: any; kpi: string },
    string
  >();
  const { authInfo } = useAppSelector((state) => state.auth);
  const departmentId = authInfo?.departmentId;
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleSearch,
    filter,
    keyword,
    handleFilter,
  } = useTable<GetOwnKpiDepartmentParams>({
    defaultFilter: {
      departmentId,
      positionStatus: "kpi",
      employeeId: authInfo?.id,
    },
  });
  const { isLoading, isError, error, data } = useGetOwnKpiByDepartmentQuery({
    params: {
      search: keyword,
      ...filter,
    },
  });
  useError(isError, error, "Get own KPI fail");

  const columns: TableProps<OwnKpiListType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      dataIndex: "kpi_title",
      render: (val) => val || "n/a",
    },

    {
      title: "Plan Period",
      dataIndex: "plan_period",
      render: (val) => findPlanVal(planPeriodList, val),
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type",
      render: (val: keyof typeof planTypeColor) => (
        <span style={planTypeColor[val] ? { color: planTypeColor[val] } : {}}>
          {findPlanVal(planTypeList, val)}
        </span>
      ),
    },
    {
      title: "Status",
      // dataIndex: "employeeKpis",
      width: 300,
      render: (_, record) => {
        const status = record?.employeeKpis?.length
          ? record?.employeeKpis?.[0]?.status
          : "default";
        const st = statusList[status];
        return <span className={st?.style}>{st?.text}</span>;
      },
    },
    {
      title: "Due Date",
      render: (_, record) => {
        const findDepartment = record?.departments?.find(
          (item) => item?.departmentId === departmentId
        );

        return findDepartment?.due_date
          ? formatDate(findDepartment?.due_date)
          : "n/a";
      },
    },

    {
      title: "Action",
      render: (_, record) => {
        return (
          <Space>
            {record?.employeeKpis?.length ? (
              <Link
                to={`/admin/kpi-setup/own-kpi/view/${record?.employeeKpis?.[0]?.id}`}
              >
                <Button
                  className="action-button"
                  size="small"
                  icon={<ViewIcon />}
                />
              </Link>
            ) : (
              <Link to={`/admin/kpi-setup/own-kpi/create/${record?.id}`}>
                <Button
                  className="h-7 middle-button hover:!border-display-light !text-primary"
                  icon={<OrangePlusIcon />}
                >
                  KPI Setup
                </Button>
              </Link>
            )}

            <Button
              icon={<ActionWatchIcon />}
              size="small"
              className="action-button"
              onClick={() =>
                handleOpen({
                  type: "OWN_KPI_LOG_VIEW",
                  data: {
                    log: record?.actionLogs,
                    kpi: record?.kpi_title,
                  },
                })
              }
            />
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <SidebarLayout title="Own KPI List">
        {open.type === "OWN_KPI_LOG_VIEW" && (
          <LogViewModalKPI
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
          />
        )}

        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.kpis)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder={`Search by Plan Name`}
                style={{ minWidth: 200, maxWidth: 210 }}
                onChange={handleSearch}
              />

              <RangePicker
                format={dateFormat}
                style={{ width: 260 }}
                suffixIcon={<DateIcon />}
                onChange={(e) => {
                  handleFilter("startDate", e?.[0]?.format(dbDateFormat));
                  handleFilter("endDate", e?.[1]?.format(dbDateFormat));
                }}
              />
              {/* <Button
                type="primary"
                className="middle-button !p-4"
                icon={<SettingIcon />}
              /> */}
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}
